// F3 — lógica PURA do failover de ENDEREÇO do provider no LivePlayerProvider.
//
// Espelha o auto-switch de família, mas a "próxima opção" é o próximo endpoint do
// provider, resolvido pelo BACKEND (o app nunca conhece a lista de endereços nem
// as credenciais). O app só pede "evite este endereço, me dá o próximo" via
// re-sign com `avoidEndpointId`.
//
// Extraído pra testar o ANTI-LOOP isoladamente (sem jsdom/React) — mesma estratégia
// do play-dedup.ts. O anti-loop é a parte crítica: esgotou os endereços = erro
// normal ao usuário, NUNCA loop infinito de re-sign.

/** Decisão de "tentar o próximo endereço?" a partir do estado da cadeia de falha. */
export type EndpointFailoverDecision =
  /** Dispara uma rodada de failover de endereço (re-sign com avoidEndpointId). */
  | { type: 'try'; avoidEndpointId: string }
  /** Não há para onde ir — para com erro normal (igual ao família esgotado). */
  | { type: 'stop'; reason: 'no-endpoint-known' | 'already-attempted' | 'limit-reached' }

interface DecideStartInput {
  /** endpointId que o ÚLTIMO sign resolveu (vem no SignedStreamUrl). null quando
   *  a resposta não trouxe o campo (backend antigo / retrocompat). */
  lastEndpointId: string | null
  /** endpointIds já tentados nesta cadeia de falha (anti-loop). */
  attemptedEndpointIds: ReadonlySet<string>
  /** Quantas rodadas de failover de endereço já dispararam nesta cadeia. */
  attemptCount: number
  /** Teto absoluto de rodadas (terminação garantida mesmo com backend patológico). */
  maxAttempts: number
}

/**
 * Decide se o app deve INICIAR uma rodada de failover de endereço, depois de
 * esgotar as fontes do canal no endpoint atual.
 *
 * Regras de anti-loop (todas levam a `stop`, nunca a um re-sign cego):
 * 1. Sem endpointId conhecido (`lastEndpointId` null) → não há o que evitar.
 *    Acontece com backend antigo cuja resposta não trazia o campo. Degrada
 *    gracioso pro auto_switch_exhausted normal.
 * 2. O endpoint atual JÁ está no set de tentados → o backend já nos mandou pra
 *    ele e voltamos; não há endereço novo. Para.
 * 3. Atingiu o teto de rodadas → para (defesa-em-profundidade).
 *
 * Caso contrário, devolve `try` com o `avoidEndpointId` = endpoint atual.
 */
export function decideEndpointFailoverStart(
  input: DecideStartInput,
): EndpointFailoverDecision {
  const { lastEndpointId, attemptedEndpointIds, attemptCount, maxAttempts } = input

  if (!lastEndpointId) return { type: 'stop', reason: 'no-endpoint-known' }
  if (attemptedEndpointIds.has(lastEndpointId)) {
    return { type: 'stop', reason: 'already-attempted' }
  }
  if (attemptCount >= maxAttempts) return { type: 'stop', reason: 'limit-reached' }

  return { type: 'try', avoidEndpointId: lastEndpointId }
}

interface DecideResolvedInput {
  /** O endpoint que o re-sign PEDIU pra evitar. */
  avoidEndpointId: string
  /** O endpointId que o backend DEVOLVEU no novo sign (null = sem campo). */
  resolvedEndpointId: string | null
  /** endpointIds já tentados nesta cadeia (inclui o que acabamos de evitar). */
  attemptedEndpointIds: ReadonlySet<string>
}

/**
 * Decide, DEPOIS que o re-sign de failover resolveu, se o novo endereço é
 * utilizável (`load`) ou se o backend esgotou os endereços (`exhausted`).
 *
 * O backend, sem endereço novo, pode devolver:
 *  - o MESMO endpoint que pedimos pra evitar (avoid de único endpoint = fluxo
 *    normal no resolver), OU
 *  - um endpoint que já tentamos antes nesta cadeia.
 * Em ambos os casos é "fim da linha": carregar de novo seria loop. Paramos.
 *
 * Se a resposta não trouxe endpointId (`resolvedEndpointId` null, backend antigo),
 * carregamos mesmo assim (não dá pra detectar loop; o set de fontes + watchdogs
 * cuidam da terminação no pior caso). É o comportamento conservador correto: não
 * regredir o failover quando o backend não dá a dica.
 */
export function decideEndpointFailoverResolved(
  input: DecideResolvedInput,
): { type: 'load' } | { type: 'exhausted' } {
  const { avoidEndpointId, resolvedEndpointId, attemptedEndpointIds } = input

  if (!resolvedEndpointId) return { type: 'load' }
  if (resolvedEndpointId === avoidEndpointId) return { type: 'exhausted' }
  if (attemptedEndpointIds.has(resolvedEndpointId)) return { type: 'exhausted' }

  return { type: 'load' }
}
